// @flow

import * as React from 'react';

import { Info } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  selectCartUUID,
  selectCartIsActive,
  type DecoratedProduct,
} from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Badge, Base, Button } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFeatureFlags } from 'src/hooks/useFeatureFlag';

import { ListRow } from '../../../../ListRow';

import LabelContent from './LabelContent';

type Props = {
  productId: DecoratedProduct['id'],
  productClaims?: number,
};

export default function ClaimsRow({
  productId,
  productClaims,
}: Props): React.MixedElement {
  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();

  const active = useAppSelector(selectCartIsActive);
  const cartId = useAppSelector(selectCartUUID);
  const { __ } = useTranslator();

  if (FF_CART_OVERVIEW_NDS) {
    const listRowProps = {
      icon: !productClaims && <Info size={20} />,
      cta: active && (
        <Button
          kind="link"
          type="button"
          to={`/cart/${cartId}/configure/${productId}/claims`}
        >
          {__('Read')}
        </Button>
      ),
      intent: productClaims ? 'muted' : 'warning',
    };
    return (
      <ListRow {...listRowProps}>
        <Base flex gap="spacing-2x" flexAlignItemsCenter>
          {__('Trademark notice')}
          {productClaims ? (
            <Badge intent="success">{__('Acknowledged')}</Badge>
          ) : (
            <Badge intent="warning">{__('Pending')}</Badge>
          )}
        </Base>
      </ListRow>
    );
  }

  // Legacy style
  return (
    <Base flex flexShrink0 gap="small" marginBottom="xsmall">
      <LabelContent label={__('Trademark notice')}>
        {productClaims ? (
          <>{__('Acknowledged')}</>
        ) : (
          <Badge intent="warning">
            {__('Please read and acknowledge the trademark notice')}
          </Badge>
        )}

        {active ? (
          <Link to={`/cart/${cartId}/configure/${productId}/claims`}>
            {__('Set')}
          </Link>
        ) : (
          <Button type="button" kind="link" disabled>
            {__('Set')}
          </Button>
        )}
      </LabelContent>
    </Base>
  );
}
